/**
 * Flare Parameter Blending Module
 * Handles smooth interpolation of flare parameters at segment boundaries using easing
 */

const MM_TO_UNIT = 0.1;

/**
 * Linear interpolation between two values
 * @param {number} a - Start value
 * @param {number} b - End value
 * @param {number} t - Interpolation factor [0, 1]
 * @returns {number} - Interpolated value
 */
function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Smoothstep easing function (ease-in-out-cubic)
 * @param {number} t - Normalized value [0, 1]
 * @returns {number} - Eased value [0, 1]
 */
function smoothstep(t) {
  // Clamp to [0, 1]
  t = Math.max(0, Math.min(1, t));
  // Smoothstep formula: t * t * (3 - 2 * t)
  return t * t * (3.0 - 2.0 * t);
}

/**
 * Sine easing function (ease-in-out-sine)
 * @param {number} t - Normalized value [0, 1]
 * @returns {number} - Eased value [0, 1]
 */
function easeInOutSine(t) {
  // Clamp to [0, 1]
  t = Math.max(0, Math.min(1, t));
  // Sine easing formula: -(cos(π * t) - 1) / 2
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

/**
 * Get blended flare parameters for a vertex at a segment boundary
 * Uses plateau-based blending: smooth edges (0-25% and 75-100%) with plateau in middle (25-75%)
 *
 * Blend zone structure:
 * - 0-25%: Smooth blend FROM left segment TO right segment
 * - 25-75%: Plateau - maintain blend value at 50% (middle between segments)
 * - 75-100%: Smooth blend continues FROM left segment TO right segment
 *
 * @param {number} vertexIndex - Vertex index in geometry
 * @param {number} segmentIndex - Assigned segment index
 * @param {Object} boundaryInfo - Boundary information from angularBoundaryData
 *   {leftSegment, rightSegment, blendWeight, angle, boundaryAngle}
 * @param {Object} flareState - Flare state from store
 * @returns {Object} - Blended parameters
 *   {heightMm, deflectMm, falloffHeightMm, falloffDeflectMm}
 */
export function getBlendedFlareParameters(
  vertexIndex,
  segmentIndex,
  boundaryInfo,
  flareState
) {
  // If no boundary info, return direct segment parameters
  if (!boundaryInfo) {
    return {
      heightMm: flareState.segmentHeightValues[segmentIndex] || 0,
      deflectMm: flareState.segmentDeflectValues[segmentIndex] || 0,
      falloffHeightMm: flareState.segmentFalloffHeightValues?.[segmentIndex] || 0,
      falloffDeflectMm: flareState.segmentFalloffDeflectValues?.[segmentIndex] || 0
    };
  }

  // Get parameters from both adjacent segments
  const leftSegmentIndex = boundaryInfo.leftSegment;
  const rightSegmentIndex = boundaryInfo.rightSegment;

  const leftHeight = flareState.segmentHeightValues[leftSegmentIndex] || 0;
  const rightHeight = flareState.segmentHeightValues[rightSegmentIndex] || 0;

  const leftDeflect = flareState.segmentDeflectValues[leftSegmentIndex] || 0;
  const rightDeflect = flareState.segmentDeflectValues[rightSegmentIndex] || 0;

  const leftFalloffHeight = flareState.segmentFalloffHeightValues?.[leftSegmentIndex] || 0;
  const rightFalloffHeight = flareState.segmentFalloffHeightValues?.[rightSegmentIndex] || 0;

  const leftFalloffDeflect = flareState.segmentFalloffDeflectValues?.[leftSegmentIndex] || 0;
  const rightFalloffDeflect = flareState.segmentFalloffDeflectValues?.[rightSegmentIndex] || 0;

  // Get blend weight (0 = fully left, 1 = fully right)
  const t = boundaryInfo.blendWeight;

  // Apply plateau-based blending:
  // 0-0.25:   Blend from left (0%) to middle (50%)
  // 0.25-0.75: Plateau at middle (50%)
  // 0.75-1.0:  Blend from middle (50%) to right (100%)
  let blendT;
  if (t < 0.25) {
    // Left blend region: remap [0, 0.25] to [0, 0.5]
    blendT = (t / 0.25) * 0.5;
  } else if (t < 0.75) {
    // Middle plateau region: stay at 0.5 blend
    blendT = 0.5;
  } else {
    // Right blend region: remap [0.75, 1.0] to [0.5, 1.0]
    blendT = 0.5 + ((t - 0.75) / 0.25) * 0.5;
  }

  // Apply smoothstep easing to the blend parameter
  const easedT = smoothstep(blendT);

  // Interpolate all parameters using eased weight
  return {
    heightMm: lerp(leftHeight, rightHeight, easedT),
    deflectMm: lerp(leftDeflect, rightDeflect, easedT),
    falloffHeightMm: lerp(leftFalloffHeight, rightFalloffHeight, easedT),
    falloffDeflectMm: lerp(leftFalloffDeflect, rightFalloffDeflect, easedT)
  };
}

/**
 * Check if a vertex is in an angular boundary zone
 * @param {number} vertexIndex - Vertex index
 * @param {Map} angularBoundaryData - Map of vertex indices to boundary info
 * @returns {Object|null} - Boundary info if vertex is in a boundary zone, null otherwise
 */
export function getBoundaryInfo(vertexIndex, angularBoundaryData) {
  if (!angularBoundaryData) return null;
  return angularBoundaryData.get(vertexIndex) || null;
}

/**
 * Get angularly blended flare parameters for a vertex
 * Implements horizontal/circumferential blending between adjacent segments:
 * - 0-25%: Blend from previous segment to current segment (ramp-up)
 * - 25-100%: Maintain current segment's values (plateau)
 *
 * Note: No ramp-down blending at segment end. The next segment handles
 * the transition at its start, preventing double-blending and spikes.
 *
 * @param {THREE.Vector3} vertexPosition - Vertex position in local space
 * @param {number} segmentIndex - Assigned segment index for this vertex
 * @param {THREE.Vector3} centroid - Boundary centroid in local space
 * @param {Array<number>} flareValues - Segment boundary positions [20, 40, 60, 80, 100]
 * @param {Object} flareState - Flare state from store
 * @returns {Object} - Blended parameters {heightMm, deflectMm, falloffHeightMm, falloffDeflectMm}
 */
export function getAngularlyBlendedParameters(
  vertexPosition,
  segmentIndex,
  centroid,
  flareValues,
  flareState
) {
  const numSegments = flareValues.length;

  // Calculate angular position of vertex relative to centroid
  const dx = vertexPosition.x - centroid.x;
  const dz = vertexPosition.z - centroid.z;

  // Compute angle in XZ plane from centroid
  // 0° at top (north/-Z) when viewed from above, clockwise progression
  let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
  if (angle < 0) angle += 360;

  // Convert to percentage [0, 100)
  const anglePercent = (angle / 360) * 100;

  // Get current segment boundaries
  const prevSegmentIndex = (segmentIndex - 1 + numSegments) % numSegments;
  const nextSegmentIndex = (segmentIndex + 1) % numSegments;

  const segmentEndPercent = flareValues[segmentIndex];
  const segmentStartPercent = segmentIndex === 0 ? flareValues[numSegments - 1] : flareValues[prevSegmentIndex];

  // Calculate segment span (handle wraparound at 0/100)
  let segmentSpan = segmentEndPercent - segmentStartPercent;
  if (segmentSpan <= 0) segmentSpan += 100;

  // Calculate position within segment [0, 100] percent
  let positionInSegment = anglePercent - segmentStartPercent;
  if (positionInSegment < 0) positionInSegment += 100;
  if (positionInSegment > segmentSpan) positionInSegment = segmentSpan;

  // Normalize to [0, 1] within segment
  const normalizedPosition = positionInSegment / segmentSpan;

  // Get blend zone thresholds from store (as percentages, convert to [0, 1])
  const blendStartThreshold = (flareState.angularBlendStartPercent || 25) / 100;
  const blendEndThreshold = (flareState.angularBlendEndPercent || 75) / 100;

  // Get current segment parameters
  const currentHeight = flareState.segmentHeightValues[segmentIndex] || 0;
  const currentDeflect = flareState.segmentDeflectValues[segmentIndex] || 0;
  const currentFalloffHeight = flareState.segmentFalloffHeightValues?.[segmentIndex] || 0;
  const currentFalloffDeflect = flareState.segmentFalloffDeflectValues?.[segmentIndex] || 0;

  // Check if in ramp-up zone (0% to blendStartThreshold)
  if (normalizedPosition < blendStartThreshold) {
    // Blend from previous segment to current segment
    const prevHeight = flareState.segmentHeightValues[prevSegmentIndex] || 0;
    const prevDeflect = flareState.segmentDeflectValues[prevSegmentIndex] || 0;
    const prevFalloffHeight = flareState.segmentFalloffHeightValues?.[prevSegmentIndex] || 0;
    const prevFalloffDeflect = flareState.segmentFalloffDeflectValues?.[prevSegmentIndex] || 0;

    // Calculate blend weight [0, 1]: 0 = fully previous, 1 = fully current
    const t = normalizedPosition / blendStartThreshold;
    const easedT = easeInOutSine(t);

    return {
      heightMm: lerp(prevHeight, currentHeight, easedT),
      deflectMm: lerp(prevDeflect, currentDeflect, easedT),
      falloffHeightMm: lerp(prevFalloffHeight, currentFalloffHeight, easedT),
      falloffDeflectMm: lerp(prevFalloffDeflect, currentFalloffDeflect, easedT)
    };
  }

  // For the rest of the segment (blendStartThreshold to 100%): return current segment's parameters
  // No ramp-down blending - the next segment will handle the transition at its start
  return {
    heightMm: currentHeight,
    deflectMm: currentDeflect,
    falloffHeightMm: currentFalloffHeight,
    falloffDeflectMm: currentFalloffDeflect
  };
}
